<template>
  <div class="cart-page">
    <NavBar />
    
    <div class="cart-container">
      <!-- 面包屑导航 -->
      <a-breadcrumb class="breadcrumb">
        <a-breadcrumb-item><a href="/">首页</a></a-breadcrumb-item>
        <a-breadcrumb-item>购物车</a-breadcrumb-item>
      </a-breadcrumb>
      
      <!-- 购物车内容 -->
      <div class="cart-content">
        <div v-if="cartItems.length === 0" class="empty-cart">
          <icon-shopping-cart :size="80" style="color: #e5e6eb;" />
          <p class="empty-text">购物车是空的</p>
          <a-button type="primary" class="go-shopping-btn" @click="$router.push('/')">
            去逛逛
          </a-button>
        </div>
        
        <div v-else class="cart-list">
          <!-- 表头 -->
          <div class="cart-header">
            <div class="header-cell checkbox-cell">
              <a-checkbox v-model="allChecked" @change="handleCheckAll">全选</a-checkbox>
            </div>
            <div class="header-cell product-cell">商品信息</div>
            <div class="header-cell price-cell">单价</div>
            <div class="header-cell quantity-cell">数量</div>
            <div class="header-cell subtotal-cell">小计</div>
            <div class="header-cell action-cell">操作</div>
          </div>
          
          <!-- 商品列表 -->
          <div class="cart-item" v-for="item in cartItems" :key="item.id">
            <div class="item-cell checkbox-cell">
              <a-checkbox v-model="item.checked" @change="updateTotalPrice" />
            </div>
            <div class="item-cell product-cell">
              <img :src="item.cover_url" :alt="item.name" class="product-image" />
              <div class="product-info">
                <p class="product-name">{{ item.name }}</p>
                <p class="product-spec">{{ item.spec || '' }}</p>
              </div>
            </div>
            <div class="item-cell price-cell">
              <span class="price">¥{{ item.price }}</span>
            </div>
            <div class="item-cell quantity-cell">
              <a-input-number 
                v-model="item.quantity" 
                :min="1" 
                :max="item.stock"
                @change="handleQuantityChange(item)"
                class="quantity-input"
              />
            </div>
            <div class="item-cell subtotal-cell">
              <span class="subtotal">¥{{ (item.price * item.quantity).toFixed(2) }}</span>
            </div>
            <div class="item-cell action-cell">
              <a-button type="text" class="delete-btn" @click="removeItem(item.id)">
                <icon-delete />
              </a-button>
            </div>
          </div>
          
          <!-- 底部操作栏 -->
          <div class="cart-footer">
            <div class="footer-left">
              <a-button type="text" class="batch-delete-btn" @click="batchDelete">
                <icon-delete />
                批量删除
              </a-button>
              <a-button type="text" class="clear-cart-btn" @click="clearCart">
                <icon-close-circle />
                清空购物车
              </a-button>
            </div>
            <div class="footer-right">
              <span class="selected-info">已选择 {{ checkedCount }} 件商品，</span>
              <span class="total-label">合计：</span>
              <span class="total-price">¥{{ totalPrice }}</span>
              <a-button type="primary" size="large" class="checkout-btn" @click="checkout">
                去结算
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 地址填写弹窗 -->
    <a-modal 
      v-model:visible="showAddressModal" 
      title="填写收货地址"
      width="500px"
      @ok="handleSubmitOrder"
      ok-text="确认下单"
      cancel-text="取消"
    >
      <a-form :model="addressForm" layout="vertical">
        <a-form-item label="收货人姓名" field="contact_name">
          <a-input v-model="addressForm.contact_name" placeholder="请输入收货人姓名" />
        </a-form-item>
        <a-form-item label="联系电话" field="contact_phone">
          <a-input v-model="addressForm.contact_phone" placeholder="请输入手机号" />
        </a-form-item>
        <a-form-item label="省份" field="province">
          <a-input v-model="addressForm.province" placeholder="请输入省份，如：北京市" />
        </a-form-item>
        <a-form-item label="详细地址" field="address">
          <a-textarea 
            v-model="addressForm.address" 
            placeholder="请输入详细地址"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </a-form-item>
        <a-form-item label="订单备注（选填）" field="remark">
          <a-textarea 
            v-model="addressForm.remark" 
            placeholder="请输入订单备注"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </a-form-item>
      </a-form>
    </a-modal>
    
    <!-- 支付二维码弹窗 -->
    <a-modal 
      v-model:visible="showPaymentModal" 
      title="扫码支付"
      width="400px"
      :footer="false"
      @cancel="showPaymentModal = false"
    >
      <div class="payment-content">
        <div class="order-info">
          <p class="order-no">订单号：{{ orderInfo?.order_no }}</p>
          <p class="order-amount">支付金额：<span class="amount">¥{{ paymentAmount }}</span></p>
        </div>
        <div class="qrcode-container">
          <img src="@/assets/zhifu.png" alt="支付二维码" class="qrcode" />
        </div>
        <p class="payment-tip">请使用微信/支付宝扫码支付</p>
      </div>
    </a-modal>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { IconShoppingCart, IconDelete, IconCloseCircle } from '@arco-design/web-vue/es/icon'
import { getCartList, updateCart, deleteCart, clearCart as clearCartAPI, createOrder } from '@/api/index.js'

export default {
  name: 'CartPage',
  components: {
    NavBar,
    Footer,
    IconShoppingCart,
    IconDelete,
    IconCloseCircle
  },
  data() {
    return {
      cartItems: [],
      allChecked: false,
      totalPrice: '0.00',
      loading: false,
      showAddressModal: false,
      showPaymentModal: false,
      addressForm: {
        contact_name: '',
        contact_phone: '',
        province: '',
        address: '',
        remark: ''
      },
      orderInfo: null,
      paymentAmount: '0.00'
    }
  },
  computed: {
    checkedCount() {
      return this.cartItems.filter(item => item.checked).length
    }
  },
  mounted() {
    this.loadCartItems()
  },
  methods: {
    async loadCartItems() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      
      this.loading = true
      try {
        const result = await getCartList(token)
        if (result.code === 1 && result.data) {
          // 转换API数据格式为页面所需格式
          this.cartItems = result.data.map(item => ({
            id: item.id,
            shopitem_id: item.shopitem_id,
            name: item.shopitem_name,
            cover_url: item.shopitem_cover,
            price: parseFloat(item.price),
            original_price: parseFloat(item.original_price || 0),
            quantity: item.quantity,
            stock: item.stock,
            checked: false // 默认不选中
          }))
          this.updateTotalPrice()
        }
      } catch (error) {
        console.error('加载购物车失败:', error)
        if (error.message && error.message.includes('请先登录')) {
          this.$message.warning('请先登录')
          this.$router.push('/login')
        }
      } finally {
        this.loading = false
      }
    },
    handleCheckAll(checked) {
      this.cartItems.forEach(item => {
        item.checked = checked
      })
      this.updateTotalPrice()
    },
    async handleQuantityChange(item) {
      const token = localStorage.getItem('token')
      try {
        const result = await updateCart({
          id: item.id,
          quantity: item.quantity
        }, token)
        
        if (result.code === 1) {
          this.updateTotalPrice()
          // 触发购物车更新事件
          window.dispatchEvent(new Event('cart-updated'))
        } else {
          this.$message.error(result.msg || '更新失败')
          // 如果更新失败，重新加载购物车
          this.loadCartItems()
        }
      } catch (error) {
        console.error('更新数量失败:', error)
        this.$message.error('更新失败，请稍后重试')
        // 如果更新失败，重新加载购物车
        this.loadCartItems()
      }
    },
    updateTotalPrice() {
      let total = 0
      let allChecked = true
      
      this.cartItems.forEach(item => {
        if (item.checked) {
          total += item.price * item.quantity
        } else {
          allChecked = false
        }
      })
      
      this.totalPrice = total.toFixed(2)
      this.allChecked = allChecked && this.cartItems.length > 0
    },
    async removeItem(id) {
      this.$modal.confirm({
        title: '确认删除',
        content: '确定要删除该商品吗？',
        okText: '确定',
        cancelText: '取消',
        onOk: async () => {
          const token = localStorage.getItem('token')
          try {
            const result = await deleteCart({ id }, token)
            if (result.code === 1) {
              this.cartItems = this.cartItems.filter(item => item.id !== id)
              this.updateTotalPrice()
              this.$message.success('删除成功')
              // 触发购物车更新事件
              window.dispatchEvent(new Event('cart-updated'))
            } else {
              this.$message.error(result.msg || '删除失败')
            }
          } catch (error) {
            console.error('删除失败:', error)
            this.$message.error('删除失败，请稍后重试')
          }
        }
      })
    },
    async batchDelete() {
      const checkedItems = this.cartItems.filter(item => item.checked)
      if (checkedItems.length === 0) {
        this.$message.warning('请先选择要删除的商品')
        return
      }
      
      this.$modal.confirm({
        title: '批量删除',
        content: `确定要删除选中的 ${checkedItems.length} 件商品吗？`,
        okText: '确定',
        cancelText: '取消',
        onOk: async () => {
          const token = localStorage.getItem('token')
          try {
            // 批量删除：逐个调用删除API
            const deletePromises = checkedItems.map(item => 
              deleteCart({ id: item.id }, token)
            )
            await Promise.all(deletePromises)
            
            this.cartItems = this.cartItems.filter(item => !item.checked)
            this.updateTotalPrice()
            this.$message.success('删除成功')
            // 触发购物车更新事件
            window.dispatchEvent(new Event('cart-updated'))
          } catch (error) {
            console.error('批量删除失败:', error)
            this.$message.error('删除失败，请稍后重试')
          }
        }
      })
    },
    async clearCart() {
      if (this.cartItems.length === 0) return
      
      this.$modal.confirm({
        title: '清空购物车',
        content: '确定要清空购物车吗？',
        okText: '确定',
        cancelText: '取消',
        onOk: async () => {
          const token = localStorage.getItem('token')
          try {
            const result = await clearCartAPI(token)
            if (result.code === 1) {
              this.cartItems = []
              this.updateTotalPrice()
              this.$message.success('购物车已清空')
              // 触发购物车更新事件
              window.dispatchEvent(new Event('cart-updated'))
            } else {
              this.$message.error(result.msg || '清空失败')
            }
          } catch (error) {
            console.error('清空购物车失败:', error)
            this.$message.error('清空失败，请稍后重试')
          }
        }
      })
    },
    checkout() {
      const checkedItems = this.cartItems.filter(item => item.checked)
      if (checkedItems.length === 0) {
        this.$message.warning('请先选择要结算的商品')
        return
      }
      
      // 显示地址填写弹窗
      this.showAddressModal = true
    },
    async handleSubmitOrder() {
      // 表单验证
      if (!this.addressForm.contact_name) {
        this.$message.warning('请输入收货人姓名')
        return
      }
      if (!this.addressForm.contact_phone) {
        this.$message.warning('请输入联系电话')
        return
      }
      const phoneReg = /^1[3-9]\d{9}$/
      if (!phoneReg.test(this.addressForm.contact_phone)) {
        this.$message.warning('请输入正确的手机号格式')
        return
      }
      if (!this.addressForm.province) {
        this.$message.warning('请输入省份')
        return
      }
      if (!this.addressForm.address) {
        this.$message.warning('请输入详细地址')
        return
      }
      
      // 保存当前支付金额（在重新加载购物车之前）
      this.paymentAmount = this.totalPrice
      
      const token = localStorage.getItem('token')
      const checkedItems = this.cartItems.filter(item => item.checked)
      const cartIds = checkedItems.map(item => item.id).join(',')
      
      try {
        const result = await createOrder({
          cart_ids: cartIds,
          ...this.addressForm
        }, token)
        
        if (result.code === 1) {
          this.$message.success('下单成功')
          this.orderInfo = result.data
          this.showAddressModal = false
          
          // 显示支付二维码弹窗
          this.showPaymentModal = true
          
          // 重新加载购物车（已结算的商品会被删除）
          this.loadCartItems()
          
          // 触发购物车更新事件
          window.dispatchEvent(new Event('cart-updated'))
        } else {
          this.$message.error(result.msg || '下单失败')
        }
      } catch (error) {
        console.error('创建订单失败:', error)
        this.$message.error('下单失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.cart-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.cart-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 40px 60px;
}

.breadcrumb {
  margin-bottom: 24px;
}

.cart-content {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* 空购物车 */
.empty-cart {
  text-align: center;
  padding: 80px 0;
}

.empty-text {
  font-size: 16px;
  color: #86909c;
  margin: 24px 0 32px;
}

.go-shopping-btn {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
  padding: 0 40px;
}

/* 购物车列表 */
.cart-header {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 2px solid #f0f0f0;
  font-weight: 600;
  color: #1d2129;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 24px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cart-item:hover {
  background: #f8f9fa;
}

.header-cell,
.item-cell {
  display: flex;
  align-items: center;
}

.checkbox-cell {
  width: 60px;
  justify-content: center;
}

.product-cell {
  flex: 1;
  min-width: 0;
}

.price-cell {
  width: 120px;
  justify-content: center;
}

.quantity-cell {
  width: 160px;
  justify-content: center;
}

.subtotal-cell {
  width: 120px;
  justify-content: center;
}

.action-cell {
  width: 80px;
  justify-content: center;
}

/* 商品信息 */
.product-cell {
  display: flex;
  gap: 16px;
}

.product-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  color: #1d2129;
  margin: 0 0 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
}

.product-spec {
  font-size: 12px;
  color: #86909c;
  margin: 0;
}

.price {
  font-size: 16px;
  color: #1d2129;
}

.quantity-input {
  width: 120px;
}

.subtotal {
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.delete-btn {
  color: #86909c;
  font-size: 18px;
}

.delete-btn:hover {
  color: #ff1493;
}

/* 底部操作栏 */
.cart-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.footer-left {
  display: flex;
  gap: 16px;
}

.batch-delete-btn,
.clear-cart-btn {
  color: #86909c;
}

.batch-delete-btn:hover,
.clear-cart-btn:hover {
  color: #ff1493;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-info {
  font-size: 14px;
  color: #666;
}

.total-label {
  font-size: 16px;
  color: #1d2129;
}

.total-price {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-right: 16px;
}

.checkout-btn {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
  padding: 0 48px;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 105, 180, 0.3);
}

.checkout-btn:hover {
  background: linear-gradient(90deg, #ff1493, #ff69b4) !important;
  box-shadow: 0 6px 16px rgba(255, 105, 180, 0.4);
}

/* 响应式 */
@media (max-width: 1024px) {
  .cart-item {
    flex-wrap: wrap;
  }
  
  .product-cell {
    flex-basis: 100%;
    margin-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .cart-container {
    padding: 20px;
  }
  
  .cart-footer {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .footer-right {
    flex-direction: column;
    align-items: stretch;
  }
  
  .checkout-btn {
    width: 100%;
  }
}
</style>

<style>
/* 确认弹窗样式 */
.arco-modal-confirm .arco-btn-primary {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
}

/* 地址弹窗样式 */
:deep(.arco-modal-footer .arco-btn-primary) {
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  border: none !important;
}

:deep(.arco-modal-footer .arco-btn-primary:hover) {
  background: linear-gradient(90deg, #ff1493, #ff69b4) !important;
}

/* 支付二维码弹窗样式 */
.payment-content {
  text-align: center;
  padding: 20px 0;
}

.order-info {
  margin-bottom: 24px;
}

.order-no {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.order-amount {
  font-size: 16px;
  color: #1d2129;
}

.amount {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #ff69b4, #ff1493);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-left: 8px;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.qrcode {
  width: 200px;
  height: 200px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}

.payment-tip {
  font-size: 14px;
  color: #86909c;
  margin-bottom: 24px;
}
</style>
